import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { Button } from "@/components/ui/button";
import { CHANNELS } from "@/components/channels";
import { BarChart3, Download, TrendingUp, Eye, Target, FileText, Users, MousePointerClick, Globe, Smartphone, ShoppingCart, PackageX, CheckCircle2, Radio, Activity, RefreshCw } from "lucide-react";

const fmtEur = (v) => `${Number(v).toLocaleString("fr-FR")} €`;
const GENDER_COLORS = ["#ec4899", "#6366f1", "#94a3b8"];

export default function Analytics() {
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState([]);
  const [channels, setChannels] = useState([]);
  const [recs, setRecs] = useState([]);
  const [trends, setTrends] = useState([]);
  const [audience, setAudience] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const refreshLive = async () => {
    setRefreshing(true);
    try {
      const [a, t] = await Promise.all([api.get("/analytics/audience"), api.get("/analytics/traffic")]);
      setAudience(a.data);
      setTraffic(t.data);
      toast.success(`Données actualisées à ${t.data.updated_at}`);
    } catch {
      toast.error("Impossible d'actualiser pour le moment");
    } finally {
      setRefreshing(false);
    }
  };

  const RefreshButton = ({ testId }) => (
    <button onClick={refreshLive} disabled={refreshing} data-testid={testId}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-background/60 text-xs font-semibold hover:bg-accent transition-colors disabled:opacity-60 shrink-0">
      <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Actualiser
    </button>
  );

  useEffect(() => {
    api.get("/analytics/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/analytics/timeseries").then((r) => setSeries(r.data.series)).catch(() => {});
    api.get("/analytics/channels").then((r) => setChannels(r.data.breakdown)).catch(() => {});
    api.get("/analytics/recommendations").then((r) => setRecs(r.data.recommendations)).catch(() => {});
    api.get("/analytics/competition").then((r) => setTrends(r.data.trends)).catch(() => {});
    api.get("/analytics/audience").then((r) => setAudience(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const load = () => api.get("/analytics/traffic").then((r) => setTraffic(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  const exportCsv = () => {
    const rows = [["Date", "CA", "Dépenses", "ROAS"], ...series.map((s) => [s.date, s.revenue, s.spend, s.roas])];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "monaga-analytics.csv"; a.click();
    toast.success("Export CSV téléchargé");
  };

  const exportPdf = async () => {
    toast.info("Génération du rapport PDF…");
    try {
      const res = await api.get("/reports/performance", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "monaga-rapport-performance.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Rapport PDF téléchargé !");
    } catch { toast.error("Échec de la génération du PDF"); }
  };

  const metrics = [
    { l: "ROAS global", v: `${overview?.roas || 0}x`, Icon: TrendingUp, c: "text-emerald-500" },
    { l: "CA attribué", v: fmtEur(overview?.revenue || 0), Icon: Target, c: "text-indigo-400" },
    { l: "CPC moyen", v: fmtEur(overview?.cpc || 0), Icon: BarChart3, c: "text-cyan-400" },
    { l: "CPA estimé", v: fmtEur(overview?.cpa || 0), Icon: Target, c: "text-pink-400" },
    { l: "CTR", v: `${overview?.ctr || 0}%`, Icon: Eye, c: "text-amber-400" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div><h1 className="font-display text-3xl font-bold flex items-center gap-2"><BarChart3 className="text-primary" /> Analytiques & intelligence</h1><p className="text-muted-foreground">Pilotage unifié et optimisation prédictive.</p></div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportPdf} className="rounded-xl" data-testid="export-pdf-btn"><FileText size={16} className="mr-1.5" /> Rapport PDF</Button>
          <Button variant="outline" onClick={exportCsv} className="rounded-xl" data-testid="export-csv-btn"><Download size={16} className="mr-1.5" /> Export CSV</Button>
        </div>
      </div>

      {/* TRAFIC EN DIRECT (RÉEL) */}
      {traffic && (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="live-traffic">
          <div className="p-5 border-b border-border flex items-center justify-between bg-gradient-to-r from-emerald-500/10 to-transparent">
            <div className="flex items-center gap-2">
              <span className="relative flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60 animate-ping" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
              </span>
              <div>
                <h2 className="font-display font-bold text-lg flex items-center gap-2"><Radio size={18} className="text-emerald-500" /> Trafic en direct</h2>
                <p className="text-xs text-muted-foreground">Visites réelles de votre site · temps réel</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="hidden sm:inline text-xs text-muted-foreground font-mono whitespace-nowrap">maj {traffic.updated_at}</span>
              <RefreshButton testId="traffic-refresh-btn" />
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x divide-border">
            <div className="p-5" data-testid="traffic-online">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1"><Activity size={13} className="text-emerald-500" /> En ligne maintenant</div>
              <p className="font-display text-3xl font-extrabold text-emerald-500">{traffic.online_now}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">visiteurs actifs (5 dernières min)</p>
            </div>
            <div className="p-5" data-testid="traffic-today">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1"><Users size={13} className="text-cyan-400" /> Visiteurs aujourd'hui</div>
              <p className="font-display text-3xl font-extrabold">{traffic.today_visitors.toLocaleString("fr-FR")}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{traffic.today_views.toLocaleString("fr-FR")} pages vues</p>
            </div>
            <div className="p-5" data-testid="traffic-week">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1"><TrendingUp size={13} className="text-indigo-400" /> Visiteurs (7 jours)</div>
              <p className="font-display text-3xl font-extrabold">{traffic.week_visitors.toLocaleString("fr-FR")}</p>
            </div>
            <div className="p-5" data-testid="traffic-total">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1"><Eye size={13} className="text-pink-400" /> Pages vues (total)</div>
              <p className="font-display text-3xl font-extrabold">{traffic.total_views.toLocaleString("fr-FR")}</p>
            </div>
          </div>
          <div className="h-36 px-2 pb-2">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={traffic.by_day}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={28} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }} formatter={(v, n) => [v, n === "visitors" ? "Visiteurs" : "Pages vues"]} />
                <Bar dataKey="visitors" fill="#10b981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {metrics.map((m) => (
          <div key={m.l} className="p-5 rounded-2xl border border-border bg-card" data-testid={`metric-${m.l.toLowerCase().replace(/\s/g, "-")}`}>
            <m.Icon size={18} className={`${m.c} mb-2`} />
            <p className="font-display text-2xl font-bold">{m.v}</p>
            <p className="text-xs text-muted-foreground">{m.l}</p>
          </div>
        ))}
      </div>

      {/* AUDIENCE */}
      {audience && (
        <div className="space-y-4" data-testid="audience-section">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-display text-xl font-bold flex items-center gap-2"><Users size={20} className="text-primary" /> Audience & visiteurs</h2>
            {audience.estimated && (
              <span className="px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-500 text-xs font-semibold" data-testid="audience-estimated-badge">Estimations</span>
            )}
            <div className="ml-auto"><RefreshButton testId="audience-refresh-btn" /></div>
          </div>
          {audience.estimated && (
            <p className="text-xs text-muted-foreground -mt-2">Répartition estimée par secteur. Connectez un compte publicitaire (Hub) pour vos données d'audience réelles.</p>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-5 rounded-2xl border border-border bg-card"><Users size={18} className="text-cyan-400 mb-2" /><p className="font-display text-2xl font-bold">{(traffic ? traffic.week_visitors : 0).toLocaleString("fr-FR")}</p><p className="text-xs text-muted-foreground">Visiteurs (7 j, réels)</p></div>
            <div className="p-5 rounded-2xl border border-border bg-card"><MousePointerClick size={18} className="text-indigo-400 mb-2" /><p className="font-display text-2xl font-bold">{audience.clicks.toLocaleString("fr-FR")}</p><p className="text-xs text-muted-foreground">Clics</p></div>
            <div className="p-5 rounded-2xl border border-border bg-card"><Eye size={18} className="text-pink-400 mb-2" /><p className="font-display text-2xl font-bold">{audience.impressions.toLocaleString("fr-FR")}</p><p className="text-xs text-muted-foreground">Impressions</p></div>
            <div className="p-5 rounded-2xl border border-border bg-card"><TrendingUp size={18} className="text-emerald-500 mb-2" /><p className="font-display text-2xl font-bold">{audience.ctr}%</p><p className="text-xs text-muted-foreground">CTR</p></div>
          </div>

          {audience.has_data ? (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Countries */}
            <div className="p-6 rounded-2xl border border-border bg-card">
              <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><Globe size={18} className="text-primary" /> Pays</h3>
              <div className="space-y-3">
                {audience.by_country.map((c) => (
                  <div key={c.country} data-testid={`country-${c.country}`}>
                    <div className="flex justify-between text-sm mb-1"><span className="flex items-center gap-2"><span className="text-base">{c.flag}</span>{c.country}</span><span className="text-muted-foreground">{c.pct}%</span></div>
                    <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-cyan-500" style={{ width: `${c.pct}%` }} /></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Gender */}
            <div className="p-6 rounded-2xl border border-border bg-card">
              <h3 className="font-display font-semibold mb-4">Genre</h3>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={audience.by_gender} dataKey="pct" nameKey="gender" cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3}>
                    {audience.by_gender.map((e, i) => <Cell key={i} fill={GENDER_COLORS[i % GENDER_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }} formatter={(v) => `${v}%`} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-4 mt-2">
                {audience.by_gender.map((g, i) => (
                  <div key={g.gender} className="flex items-center gap-1.5 text-xs"><span className="h-2.5 w-2.5 rounded-full" style={{ background: GENDER_COLORS[i] }} />{g.gender} {g.pct}%</div>
                ))}
              </div>
            </div>

            {/* Age + Device */}
            <div className="p-6 rounded-2xl border border-border bg-card">
              <h3 className="font-display font-semibold mb-4">Tranches d'âge</h3>
              <div className="space-y-2.5 mb-5">
                {audience.by_age.map((a) => (
                  <div key={a.range}>
                    <div className="flex justify-between text-xs mb-1"><span>{a.range}</span><span className="text-muted-foreground">{a.pct}%</span></div>
                    <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-pink-500 to-amber-400" style={{ width: `${a.pct}%` }} /></div>
                  </div>
                ))}
              </div>
              <h3 className="font-display font-semibold mb-3 flex items-center gap-2"><Smartphone size={16} className="text-cyan-400" /> Appareils</h3>
              <div className="flex gap-2">
                {audience.by_device.map((d) => (
                  <div key={d.device} className="flex-1 text-center p-2 rounded-xl bg-secondary/60"><p className="font-display font-bold text-sm">{d.pct}%</p><p className="text-[10px] text-muted-foreground">{d.device}</p></div>
                ))}
              </div>
            </div>
          </div>
          ) : (
            <div className="p-8 rounded-2xl border border-dashed border-border bg-card text-center" data-testid="audience-empty">
              <Globe size={28} className="text-muted-foreground mx-auto mb-3" />
              <p className="font-display font-semibold">Données d'audience réelles indisponibles</p>
              <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">Connectez un compte publicitaire (Meta, TikTok, Google Ads) depuis le Hub pour afficher la répartition réelle par pays, âge, genre et appareil.</p>
            </div>
          )}

          {audience.funnel && (
            <div className="grid lg:grid-cols-3 gap-6" data-testid="funnel-section">
              <div className="lg:col-span-2 p-6 rounded-2xl border border-border bg-card">
                <h3 className="font-display font-semibold mb-5 flex items-center gap-2"><Target size={18} className="text-primary" /> Entonnoir de conversion</h3>
                <div className="space-y-3">
                  {audience.funnel.map((f, i) => (
                    <div key={f.step} data-testid={`funnel-${i}`}>
                      <div className="flex justify-between text-sm mb-1"><span className="font-medium">{f.step}</span><span className="text-muted-foreground">{f.value.toLocaleString("fr-FR")} · {f.pct}%</span></div>
                      <div className="h-7 rounded-lg bg-secondary overflow-hidden">
                        <div className="h-full rounded-lg bg-gradient-to-r from-indigo-500 via-cyan-500 to-emerald-500 flex items-center justify-end pr-2 transition-all duration-700" style={{ width: `${Math.max(f.pct, 6)}%` }}>
                          <span className="text-[10px] font-bold text-white">{f.pct}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-4">
                <div className="p-5 rounded-2xl border border-border bg-card"><ShoppingCart size={18} className="text-indigo-400 mb-2" /><p className="font-display text-2xl font-bold">{audience.carts.toLocaleString("fr-FR")}</p><p className="text-xs text-muted-foreground">Paiements initiés</p></div>
                <div className="p-5 rounded-2xl border border-border bg-card"><CheckCircle2 size={18} className="text-emerald-500 mb-2" /><p className="font-display text-2xl font-bold">{audience.buyers.toLocaleString("fr-FR")}</p><p className="text-xs text-muted-foreground">Abonnés payants</p></div>
                <div className="p-5 rounded-2xl border border-border bg-card"><PackageX size={18} className="text-destructive mb-2" /><p className="font-display text-2xl font-bold">{audience.cart_abandon_rate}%</p><p className="text-xs text-muted-foreground">Paiements non aboutis ({audience.abandoned_carts})</p></div>
              </div>
            </div>
          )}

        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 p-6 rounded-2xl border border-border bg-card">
          <h3 className="font-display font-semibold mb-4">Chiffre d'affaires & dépenses (30 j)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={series}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6366f1" stopOpacity={0.5} /><stop offset="100%" stopColor="#6366f1" stopOpacity={0} /></linearGradient>
                <linearGradient id="sp" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#06b6d4" stopOpacity={0.4} /><stop offset="100%" stopColor="#06b6d4" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} interval={5} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
              <Area type="monotone" dataKey="revenue" stroke="#6366f1" fill="url(#rev)" strokeWidth={2} name="CA" />
              <Area type="monotone" dataKey="spend" stroke="#06b6d4" fill="url(#sp)" strokeWidth={2} name="Dépenses" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="p-6 rounded-2xl border border-border bg-card">
          <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><TrendingUp size={18} className="text-primary" /> Recommandations IA</h3>
          <div className="space-y-2.5 max-h-[280px] overflow-y-auto">
            {recs.map((r, i) => (
              <div key={i} className={`p-3 rounded-xl text-sm border ${r.severity === "success" ? "border-emerald-500/30 bg-emerald-500/5" : r.severity === "danger" ? "border-destructive/30 bg-destructive/5" : r.severity === "warning" ? "border-amber-500/30 bg-amber-500/5" : "border-border"}`}>{r.message}</div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="p-6 rounded-2xl border border-border bg-card">
          <h3 className="font-display font-semibold mb-4">Performance par réseau (attribution)</h3>
          {channels.length === 0 ? <p className="text-sm text-muted-foreground py-10 text-center">Lancez des campagnes pour voir l'attribution multi-touch.</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={channels}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="channel" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => CHANNELS[v]?.name.split(" ")[0] || v} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                <Bar dataKey="revenue" fill="#6366f1" radius={[6, 6, 0, 0]} name="CA" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="p-6 rounded-2xl border border-border bg-card">
          <h3 className="font-display font-semibold mb-4">Veille concurrentielle — tendances</h3>
          <div className="space-y-3">
            {trends.map((t) => (
              <div key={t.trend}>
                <div className="flex justify-between text-sm mb-1"><span className="font-medium">{t.trend}</span><span className="text-muted-foreground text-xs">{t.note}</span></div>
                <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-cyan-500" style={{ width: `${t.momentum}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
